import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { getLtcPool } from '@shared/calculations/funding/projection/projectionTotals';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { appliedCapTooltipInputs, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import { AccumulationWhenCell, GrowthValueCell } from '../../accumulationTimeline/cells';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';
import {
  careOutOfPocketReasons,
  payoutBreakdownRows,
  reimbursementPayoutRows,
} from './payoutBreakdown';
import type { StrategyPresentation } from './types';

// Life Insurance with LTC Rider — a single death-benefit pool accelerated for
// LTC. The death benefit IS the pool: LTC draws reduce the DB dollar-for-dollar
// (floored at the Residual Death Benefit). There is NO continuation pool, so
// the presentation drops the AOB/COB framing entirely (mirrors a single-pool
// product) while keeping the death-benefit cell. Serves the generic
// `lifeInsuranceWithRider`, Transamerica FFIUL (`lifeWithIndexAccount`), and
// Nationwide No-Lapse Guarantee UL II (`noLapseGuaranteeLife`). All values are
// read off precomputed month state/deltas — no strategy math is re-implemented.

const hasTimelineValue = (
  timeline: AccumulationYear[],
  pick: (snapshot: YearSnapshot) => number,
): boolean => timeline.some(row => pick(row.snapshot) > 0.5);

function lifeWithRiderInflationTooltip({
  row,
  startingLabel,
  startingValue,
  rate,
  capYears,
  isSimple,
  result,
  compoundLabel,
}: {
  row: AccumulationYear;
  startingLabel: string;
  startingValue: number;
  rate: number;
  capYears: number;
  isSimple: boolean;
  result: number;
  compoundLabel: string;
}) {
  const policyYear = Math.floor(row.policyYear);
  const cap = capYears > 0 ? capYears : null;
  const rawIncrements = Math.max(policyYear - 1, 0);
  const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
  const capped = cap !== null && rawIncrements > cap;

  return (
    <InflationTooltipContent
      header={`Policy year ${policyYear}`}
      startingLabel={startingLabel}
      startingValue={startingValue}
      rate={rate}
      years={increments}
      result={result}
      note={capped ? `Inflation capped at year ${cap}` : undefined}
      compoundLabel={compoundLabel}
      isSimple={isSimple}
    />
  );
}

function LifeWithRiderYearValueTooltip({
  row,
  title,
  description,
  startingLabel,
  startingValue,
  result,
  driverLabel,
}: {
  row: AccumulationYear;
  title: string;
  description: string;
  startingLabel: string;
  startingValue: number;
  result: number;
  driverLabel: string;
}) {
  return (
    <TooltipContent className="max-w-xs text-xs">
      <div className="mb-1 font-semibold">
        Policy year {Math.floor(row.policyYear)} · {title}
      </div>
      <div className="mb-2 text-[10px] text-gray-300">{description}</div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">This policy year</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        <span className="text-gray-300">Driven by</span>
        <span className="font-semibold">{driverLabel}</span>
      </div>
    </TooltipContent>
  );
}

function buildLifeWithRiderAccumulationColumns({
  terms,
  timeline,
}: AccumulationCtx): AccumulationColumnSpec[] {
  const poolRate = terms.baseBenefitPoolInflationRate ?? terms.inflationRate;
  const poolCapYears = terms.baseBenefitPoolInflationCapYears ?? terms.inflationCapYears;
  const isSimple = terms.inflationIsSimple === true;
  const firstSnapshot = timeline[0]?.snapshot;
  const ltcPoolAtIssue =
    terms.baseBenefitPoolAtIssue ?? firstSnapshot?.theoreticalLtcPoolCeiling ?? 0;
  const monthlyBenefitAtIssue =
    terms.nominalDailyBenefitBase * DAYS_IN_MONTH || firstSnapshot?.monthlyBenefitCap || 0;
  const deathBenefitAtIssue = firstSnapshot?.effectiveFaceAmount ?? ltcPoolAtIssue;

  const columns: AccumulationColumnSpec[] = [
    {
      key: 'when',
      label: 'Policy year',
      sticky: true,
      render: row => <AccumulationWhenCell row={row} />,
    },
  ];

  if (!terms.isUnlimited && hasTimelineValue(timeline, s => s.theoreticalLtcPoolCeiling)) {
    columns.push({
      key: 'ltc-pool',
      label: 'LTC pool',
      tooltip: (
        <HeaderTooltip
          description="LTC dollars available before care begins. For these policies, the death benefit is the LTC pool; there is no separate continuation pool."
          formulas={[
            {
              label: 'LTC pool',
              expression: (
                <>
                  face amount at issue ×{' '}
                  <GrowthFactor
                    rate="pool inflation rate"
                    exponent={
                      poolCapYears > 0 ? `min(policy year − 1, ${poolCapYears})` : 'policy year − 1'
                    }
                    simple={isSimple}
                  />
                </>
              ),
            },
          ]}
          notes="After care begins, LTC payments reduce this pool and the death benefit dollar-for-dollar."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.theoreticalLtcPoolCeiling}
          prevValue={prev?.snapshot.theoreticalLtcPoolCeiling ?? null}
          tooltip={lifeWithRiderInflationTooltip({
            row,
            startingLabel: `Face amount at issue (${terms.policyPremiumStartYear})`,
            startingValue: ltcPoolAtIssue,
            rate: poolRate,
            capYears: poolCapYears,
            isSimple,
            result: row.snapshot.theoreticalLtcPoolCeiling,
            compoundLabel: 'LTC pool inflation',
          })}
        />
      ),
    });
  }

  if (hasTimelineValue(timeline, s => s.monthlyBenefitCap)) {
    columns.push({
      key: 'monthly-benefit',
      label: 'Monthly benefit',
      tooltip: (
        <HeaderTooltip
          description="Maximum monthly LTC benefit before care begins. It starts with the highest configured daily benefit times 30 and grows on the pool inflation rate."
          formulas={[
            {
              label: 'Monthly benefit',
              expression: (
                <>
                  at-issue monthly benefit ×{' '}
                  <GrowthFactor
                    rate="pool inflation rate"
                    exponent={
                      poolCapYears > 0 ? `min(policy year − 1, ${poolCapYears})` : 'policy year − 1'
                    }
                    simple={isSimple}
                  />
                </>
              ),
            },
          ]}
          notes="Monthly claim rows can use care-setting-specific daily benefits; this table shows the policy maximum before care."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.monthlyBenefitCap}
          prevValue={prev?.snapshot.monthlyBenefitCap ?? null}
          tooltip={lifeWithRiderInflationTooltip({
            row,
            startingLabel: `Monthly benefit at issue (${terms.policyPremiumStartYear})`,
            startingValue: monthlyBenefitAtIssue,
            rate: poolRate,
            capYears: poolCapYears,
            isSimple,
            result: row.snapshot.monthlyBenefitCap,
            compoundLabel: 'Pool inflation',
          })}
        />
      ),
    });
  }

  if (hasTimelineValue(timeline, s => s.effectiveFaceAmount)) {
    columns.push({
      key: 'death-benefit',
      label: 'Death benefit',
      tooltip: (
        <HeaderTooltip
          description="Death benefit before care begins. The displayed value is the policy year's scheduled death benefit before any LTC draw."
          formulas={[
            {
              label: 'Displayed',
              expression: 'scheduled death benefit before LTC draw',
            },
          ]}
          notes="The strategy applies the policy's death-benefit / face inflation option when one is configured. LTC payments later reduce the death benefit dollar-for-dollar, down to any residual death-benefit guarantee."
        />
      ),
      render: (row, prev) => (
        <GrowthValueCell
          value={row.snapshot.effectiveFaceAmount}
          prevValue={prev?.snapshot.effectiveFaceAmount ?? null}
          tooltip={
            <LifeWithRiderYearValueTooltip
              row={row}
              title="death benefit"
              description="Death benefit before any LTC draw in this timeline."
              startingLabel={`Face amount at issue (${terms.policyPremiumStartYear})`}
              startingValue={deathBenefitAtIssue}
              result={row.snapshot.effectiveFaceAmount}
              driverLabel="Death-benefit / face inflation option"
            />
          }
        />
      ),
    });
  }

  return columns;
}

export const lifeWithRiderPresentation: StrategyPresentation = {
  accumulationColumns: buildLifeWithRiderAccumulationColumns,

  // The monthly cap is per care environment and inflates on the pool rate, so
  // the cell headlines the engine's own applied cap; the tooltip's
  // base × (1+r)^n derivation is reconstructed from terms for display only.
  benefitAmountCell: ({ month, terms }) => {
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;
    const { baseMonthly, rate, capYears } = appliedCapTooltipInputs(month, terms);
    const intYear = Math.floor(month.context.policyYear);
    const cap = capYears && capYears > 0 ? capYears : null;
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help leading-tight">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear}`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={rate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Inflation capped at year ${cap}` : undefined}
          compoundLabel="Compound growth"
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  // "Pool remaining" — the single accelerable pool open → close breakdown.
  poolCellTooltip: ({ month, openState }) => {
    const opening = getLtcPool(openState);
    const closing = getLtcPool(month.state);
    const accrual = month.deltas.base.inflationAccrual;
    const drawn = month.deltas.base.depletion;

    if (opening === 0 && closing === 0 && accrual === 0 && drawn === 0) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC acceleration pool"
        description="The death benefit is accelerated to pay for LTC from a single pool. Drawing for LTC reduces both this pool and the death benefit. Inflation accrues at policy anniversaries while the pool remains open."
        sections={[
          {
            opening,
            closing,
            lines: [
              { label: 'Inflation accrual', value: accrual, kind: 'add' },
              { label: 'Drawn this month', value: drawn, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  // "Death benefit" — the residual DB, reduced dollar-for-dollar by the pool
  // drawdown and floored at the Residual Death Benefit.
  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const reduction = month.deltas.deathBenefit.reduction;
    const flooredAtMinimum = month.deltas.deathBenefit.flooredAtMinimum;
    const baseDrawdown = month.deltas.base.depletion;

    if (open === 0 && close === 0 && reduction === 0) return null;

    const floorNote = flooredAtMinimum
      ? `LTC drew ${currency(baseDrawdown)}; death benefit reduction capped at the Residual Death Benefit ${currency(close)}.`
      : undefined;

    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="LTC acceleration reduces the death benefit dollar-for-dollar. Whatever remains at death is paid to beneficiaries, floored at the Residual Death Benefit."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [{ label: 'Reduced by LTC draw', value: reduction, kind: 'subtract' }],
            note: floorNote,
          },
        ]}
      />
    );
  },

  // "Projected payout" — reimbursement (or cash indemnity) waterfall. Single
  // pool, so there is no AOB/COB split line.
  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Keep the tooltip alive on a fully-ineligible month (waiting period / <2
    // ADL) when room & board is still out-of-pocket — Independent Living books
    // R&B even on non-paying months (cmTwo guards the same way); it's the exact
    // OOP this modeling exists to surface. Otherwise PaidCell shows the
    // zero-row reason natively.
    if (eligibleDays === 0 && (month.facts.roomAndBoardCost ?? 0) === 0) return null;

    const cost = month.facts.effectiveCareCost;
    const dailyCost = month.facts.dailyCareCost;
    const drawn = month.deltas.base.depletion;
    const isPartial = eligibleDays < DAYS_IN_MONTH;
    const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;

    const isIndemnity = terms.isIndemnity;
    const poolExhausted = getLtcPool(month.state) === 0;
    const roomAndBoard = month.facts.roomAndBoardCost ?? 0;
    // Indemnity cash can meet part of room & board (0 for reimbursement, so the
    // OOP nets to the full room & board there); only the unmet part is OOP.
    const roomAndBoardCovered = month.deltas.payout.roomAndBoardCovered ?? 0;
    const roomAndBoardOop = Math.max(roomAndBoard - roomAndBoardCovered, 0);

    const mode = isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = isIndemnity
      ? 'Pays a fixed cash benefit up to the monthly cap regardless of actual cost, accelerated from the death benefit. Cash above the eligible cost banks as a surplus reserve, drained on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the monthly cap, accelerated from the death benefit.';

    // Out-of-pocket "over cap / pool exhausted" line — the eligible cost the
    // policy did not cover this month. Indemnity covers cost up to
    // `ltcPayoutToCost` (capped coverage + surplus drained); reimbursement
    // covers up to `ltcCoveragePaid`.
    const coveredToCost = isIndemnity
      ? month.deltas.payout.ltcPayoutToCost
      : month.deltas.payout.ltcCoveragePaid;
    const eligibleUnpaid = Math.max(eligibleCost - coveredToCost, 0);


    // Indemnity: cross-foot against the surplus model. Identities honored:
    //   indemnityPayout = ltcCoveragePaid + accrued (cash above cost banks)
    //   ltcPayoutToCost = ltcCoveragePaid + applied  (surplus drained to cost)
    // So "Applied to cost" = ltcPayoutToCost and "Banked as surplus" = accrued.
    if (isIndemnity) {
      const sd = month.deltas.strategyDeltas as {
        indemnitySurplusAccrued?: number;
        indemnitySurplusApplied?: number;
      };
      const accrued = sd.indemnitySurplusAccrued ?? 0;
      const indemnityPayout = month.deltas.payout.indemnityPayout;

      return (
        <TooltipContent className="max-w-xs text-xs">
          <div className="mb-1 font-semibold">Projected payout — {mode}</div>
          <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
          <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
            {payoutBreakdownRows({
              paidByPolicy: indemnityPayout,
              careCovered: coveredToCost,
              roomAndBoardCovered,
              surplusBanked: accrued,
              careOutOfPocket: Math.max(cost - coveredToCost, 0),
              careOutOfPocketReasons: careOutOfPocketReasons({
                month,
                overCapAmount: eligibleUnpaid,
                overCapLabel: poolExhausted ? '· benefit pool used up' : '· over monthly cap',
              }),
              roomAndBoardOutOfPocket: roomAndBoardOop,
              hasRoomAndBoard: roomAndBoard > 0,
            })}
          </div>
          <div className="mt-1 text-[10px] text-gray-400">
            Accelerated from death benefit {currency(drawn)}
          </div>
        </TooltipContent>
      );
    }

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {reimbursementPayoutRows({
            month,
            overCapLabel: poolExhausted ? '· benefit pool used up' : '· over monthly cap',
          })}
        </div>
        <div className="mt-1 text-[10px] text-gray-400">
          Accelerated from death benefit {currency(drawn)}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const isHomeBucket = month.context.careEnvironment === CareEnvironment.home;
    const wpConsumed = isHomeBucket
      ? month.facts.waitingPeriodDaysConsumedHome
      : month.facts.waitingPeriodDaysConsumedFacility;

    if (!(wpConsumed > 0 && month.deltas.payout.ltcCoveragePaid === 0)) return null;

    return (
      <div className="mt-0.5 text-[10px] text-amber-700">
        {`${isHomeBucket ? 'Home' : 'Facility'} WP serving ${wpConsumed}d`}
      </div>
    );
  },

  // Single accelerable pool — drop the AOB/COB framing in the Drawn cell.
  drawnCellLabels: {
    description:
      'Cumulative dollars accelerated from the death benefit for LTC. ' +
      'Drawn = prior utilization + Σ (monthly pool depletion).',
    baseLabel: 'Accelerated for LTC',
    singlePool: true,
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC acceleration pool balance, with this month's change beneath. The death benefit is accelerated for LTC from a single pool; the remaining balance grows on each policy anniversary while the pool is open."
        formulas={[
          { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
          {
            label: 'Inflation accrual',
            expression: 'at-issue face × pool inflation rate (at each anniversary)',
          },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly cap, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly cap, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays the cap as cash regardless of actual cost; cash above the cost builds a surplus drained on later shortfall months."
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. LTC acceleration reduces the death benefit dollar-for-dollar; whatever remains is paid to beneficiaries."
        formulas={[
          { label: 'Death benefit', expression: 'inflated face − Σ (LTC accelerated)' },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
        notes="The death benefit inflates on the face rate when an inflation option is elected."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost ×{' '}
                <GrowthFactor
                  rate="care-cost inflation"
                  exponent="years from today"
                />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this care environment and policy year, with the daily equivalent beneath. The monthly cap inflates on the pool inflation rate when an option is elected."
        formulas={[
          {
            label: 'Monthly cap',
            expression: (
              <>
                at-issue daily for environment × days in month ×{' '}
                <GrowthFactor
                  rate="pool inflation rate"
                  exponent="policy year − 1"
                />
              </>
            ),
          },
        ]}
        notes="Inflation capped at the configured cap year if specified."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative dollars accelerated from the death benefit for LTC through this month — any utilization before the projection plus this projection's drawdown."
        formulas={[
          { label: 'Drawn', expression: 'prior utilization + Σ (monthly pool depletion)' },
          {
            label: 'Identity',
            expression: 'Pool limit − Drawn = Pool remaining (while pool is open)',
          },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound growth',
    benefitInflationCapNote: capYear => `Inflation capped at year ${capYear}`,
  },
};
